/**
 * Sky: gradient dome, round sun and moon, stars.
 *
 * Deliberately not Minecraft's look. Three things do most of that work:
 * the sun and moon are soft-edged discs with a glow rather than flat squares,
 * the sky is a vertical gradient rather than one flat colour, and the terrain
 * picks up a warm tint at sunrise and sunset.
 *
 * All of this is presentation. The timekeeping lives in core/world/daynight.ts
 * and knows nothing about any of it.
 */

import * as THREE from "three";
import type { DayNightCycle } from "../core/world/daynight.js";

/** Distances from the viewer. Camera far plane is 500, so all of this fits. */
const DOME_RADIUS = 450;
const STAR_RADIUS = 420;
const BODY_DISTANCE = 380;

const SUN_SIZE = 62;
const MOON_SIZE = 48;

// ---------------------------------------------------------------------------
// Colour keyframes, sampled by sun elevation (-1 at midnight, +1 at noon)
// ---------------------------------------------------------------------------

type Keyframe = readonly [elevation: number, hex: number];

/** Sample a keyframe list, interpolating in linear RGB. */
function sampleGradient(keys: readonly Keyframe[], e: number, out: THREE.Color): THREE.Color {
  if (e <= keys[0]![0]) return out.setHex(keys[0]![1]);
  const last = keys[keys.length - 1]!;
  if (e >= last[0]) return out.setHex(last[1]);

  for (let i = 0; i < keys.length - 1; i++) {
    const [e0, c0] = keys[i]!;
    const [e1, c1] = keys[i + 1]!;
    if (e >= e0 && e <= e1) {
      const t = (e - e0) / (e1 - e0);
      return out.setHex(c0).lerp(TMP.setHex(c1), t);
    }
  }
  return out.setHex(last[1]);
}

const TMP = new THREE.Color();

/** Top of the sky. */
const ZENITH: readonly Keyframe[] = [
  [-1.00, 0x050916],
  [-0.30, 0x0b1230],
  [-0.10, 0x22305e],
  [ 0.02, 0x40598f],
  [ 0.18, 0x4a86c4],
  [ 1.00, 0x3f86d6],
];

/** Horizon band - where sunrise and sunset actually happen. */
const HORIZON: readonly Keyframe[] = [
  [-1.00, 0x0a1128],
  [-0.30, 0x1b2245],
  [-0.12, 0x63456a],
  [-0.02, 0xd4703f],
  [ 0.06, 0xf0a367],
  [ 0.22, 0xbcd8ee],
  [ 1.00, 0xcfe4f5],
];

/**
 * Multiplied over the terrain texture. Gives golden-hour light at the edges
 * of the day and cool blue moonlight at night.
 */
const WORLD_TINT: readonly Keyframe[] = [
  [-1.00, 0x39456b],
  [-0.25, 0x4a5478],
  [-0.05, 0x9c7d84],
  [ 0.05, 0xe0a179],
  [ 0.20, 0xf6efe4],
  [ 1.00, 0xffffff],
];

// ---------------------------------------------------------------------------
// Procedural textures for the celestial bodies
// ---------------------------------------------------------------------------

function makeCanvas(size: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("2D canvas context unavailable");
  return [canvas, ctx];
}

/** Warm disc with a soft corona. Round, and softly so - no stair-stepped edge. */
function makeSunTexture(): THREE.Texture {
  const S = 256;
  const [canvas, ctx] = makeCanvas(S);
  const c = S / 2;

  const glow = ctx.createRadialGradient(c, c, 0, c, c, c);
  glow.addColorStop(0.00, "rgba(255, 252, 235, 1)");
  glow.addColorStop(0.22, "rgba(255, 243, 200, 1)");
  glow.addColorStop(0.30, "rgba(255, 214, 128, 0.55)");
  glow.addColorStop(0.46, "rgba(255, 176, 84, 0.20)");
  glow.addColorStop(0.72, "rgba(255, 150, 70, 0.05)");
  glow.addColorStop(1.00, "rgba(255, 140, 60, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, S, S);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Pale disc with faint maria and a thin halo. */
function makeMoonTexture(): THREE.Texture {
  const S = 256;
  const [canvas, ctx] = makeCanvas(S);
  const c = S / 2;

  // Outer halo.
  const halo = ctx.createRadialGradient(c, c, 0, c, c, c);
  halo.addColorStop(0.00, "rgba(214, 226, 245, 0.55)");
  halo.addColorStop(0.34, "rgba(200, 214, 240, 0.30)");
  halo.addColorStop(0.60, "rgba(170, 190, 230, 0.07)");
  halo.addColorStop(1.00, "rgba(150, 175, 220, 0)");
  ctx.fillStyle = halo;
  ctx.fillRect(0, 0, S, S);

  // The disc itself, very slightly shaded so it reads as a sphere.
  const disc = ctx.createRadialGradient(c * 0.86, c * 0.84, S * 0.04, c, c, S * 0.33);
  disc.addColorStop(0.0, "#f4f7ff");
  disc.addColorStop(0.7, "#dfe6f4");
  disc.addColorStop(1.0, "#c3cee2");
  ctx.beginPath();
  ctx.arc(c, c, S * 0.33, 0, Math.PI * 2);
  ctx.fillStyle = disc;
  ctx.fill();

  // Maria - a few soft dark patches. Fixed positions, so the moon has a face.
  const maria: readonly [number, number, number, number][] = [
    [-0.10, -0.09, 0.115, 0.16],
    [ 0.10, -0.02, 0.075, 0.13],
    [-0.02,  0.12, 0.095, 0.11],
    [ 0.14,  0.15, 0.055, 0.09],
    [-0.17,  0.05, 0.050, 0.10],
  ];
  ctx.save();
  ctx.beginPath();
  ctx.arc(c, c, S * 0.33, 0, Math.PI * 2);
  ctx.clip(); // keep patches inside the disc
  for (const [dx, dy, r, alpha] of maria) {
    const g = ctx.createRadialGradient(
      c + dx * S, c + dy * S, 0,
      c + dx * S, c + dy * S, r * S,
    );
    g.addColorStop(0, `rgba(128, 142, 168, ${alpha})`);
    g.addColorStop(1, "rgba(128, 142, 168, 0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, S, S);
  }
  ctx.restore();

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// ---------------------------------------------------------------------------

const DOME_VERTEX = /* glsl */ `
  varying vec3 vDirection;
  void main() {
    vDirection = normalize(position);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const DOME_FRAGMENT = /* glsl */ `
  uniform vec3 uZenith;
  uniform vec3 uHorizon;
  varying vec3 vDirection;

  void main() {
    // pow() biases the blend toward the horizon so the warm band stays
    // narrow at sunset instead of washing over the whole sky.
    float h = pow(max(vDirection.y, 0.0), 0.42);
    gl_FragColor = vec4(mix(uHorizon, uZenith, h), 1.0);
  }
`;

/** Random points on a sphere, evenly distributed (not clustered at the poles). */
function makeStarGeometry(count: number, radius: number): THREE.BufferGeometry {
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    // Inverse-transform sampling on z gives uniform area coverage.
    const z = Math.random() * 2 - 1;
    const theta = Math.random() * Math.PI * 2;
    const r = Math.sqrt(1 - z * z);
    positions[i * 3] = Math.cos(theta) * r * radius;
    positions[i * 3 + 1] = z * radius;
    positions[i * 3 + 2] = Math.sin(theta) * r * radius;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geo;
}

export class Sky {
  /** Everything lives in here; the group is parked on the camera each frame. */
  readonly group = new THREE.Group();

  private readonly domeMaterial: THREE.ShaderMaterial;
  private readonly sun: THREE.Mesh;
  private readonly moon: THREE.Mesh;
  private readonly starsFaint: THREE.Points;
  private readonly starsBright: THREE.Points;

  /** Recomputed every update. Read by the renderer for fog and terrain tint. */
  readonly horizonColor = new THREE.Color();
  readonly zenithColor = new THREE.Color();
  readonly worldTint = new THREE.Color();

  constructor() {
    // The group must not be culled - it is always exactly around the camera.
    this.group.frustumCulled = false;

    // --- dome ------------------------------------------------------------
    this.domeMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uZenith: { value: new THREE.Color(0x3f86d6) },
        uHorizon: { value: new THREE.Color(0xcfe4f5) },
      },
      vertexShader: DOME_VERTEX,
      fragmentShader: DOME_FRAGMENT,
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
    });
    const dome = new THREE.Mesh(
      new THREE.SphereGeometry(DOME_RADIUS, 32, 16),
      this.domeMaterial,
    );
    dome.renderOrder = -3;
    dome.frustumCulled = false;
    this.group.add(dome);

    // --- stars -----------------------------------------------------------
    this.starsFaint = new THREE.Points(
      makeStarGeometry(1400, STAR_RADIUS),
      new THREE.PointsMaterial({
        color: 0xc8d4f0, size: 1.6, sizeAttenuation: false,
        transparent: true, opacity: 0, depthWrite: false, fog: false,
      }),
    );
    this.starsBright = new THREE.Points(
      makeStarGeometry(160, STAR_RADIUS),
      new THREE.PointsMaterial({
        color: 0xffffff, size: 2.9, sizeAttenuation: false,
        transparent: true, opacity: 0, depthWrite: false, fog: false,
      }),
    );
    for (const s of [this.starsFaint, this.starsBright]) {
      s.renderOrder = -2;
      s.frustumCulled = false;
      this.group.add(s);
    }

    // --- sun and moon ----------------------------------------------------
    this.sun = new THREE.Mesh(
      new THREE.PlaneGeometry(SUN_SIZE, SUN_SIZE),
      new THREE.MeshBasicMaterial({
        map: makeSunTexture(),
        transparent: true,
        blending: THREE.AdditiveBlending, // reads as light, not a sticker
        depthWrite: false,
        fog: false,
      }),
    );
    this.moon = new THREE.Mesh(
      new THREE.PlaneGeometry(MOON_SIZE, MOON_SIZE),
      new THREE.MeshBasicMaterial({
        map: makeMoonTexture(),
        transparent: true,
        depthWrite: false,
        fog: false,
      }),
    );
    for (const body of [this.sun, this.moon]) {
      body.renderOrder = -1;
      body.frustumCulled = false;
      this.group.add(body);
    }
  }

  /**
   * Position the sky for this frame.
   *
   * `cameraPosition` keeps the dome centred on the viewer so it can never be
   * approached, and gives the billboards something to face.
   */
  update(cycle: DayNightCycle, cameraPosition: THREE.Vector3): void {
    const e = cycle.sunElevation;

    sampleGradient(ZENITH, e, this.zenithColor);
    sampleGradient(HORIZON, e, this.horizonColor);
    sampleGradient(WORLD_TINT, e, this.worldTint);

    (this.domeMaterial.uniforms.uZenith!.value as THREE.Color).copy(this.zenithColor);
    (this.domeMaterial.uniforms.uHorizon!.value as THREE.Color).copy(this.horizonColor);

    this.group.position.copy(cameraPosition);

    const sunDir = cycle.sunDirection;
    this.sun.position.set(
      sunDir.x * BODY_DISTANCE,
      sunDir.y * BODY_DISTANCE,
      sunDir.z * BODY_DISTANCE,
    );
    const moonDir = cycle.moonDirection;
    this.moon.position.set(
      moonDir.x * BODY_DISTANCE,
      moonDir.y * BODY_DISTANCE,
      moonDir.z * BODY_DISTANCE,
    );

    // Billboard both bodies toward the viewer. Positions are group-local and
    // the group sits on the camera, so the origin is the viewer.
    this.sun.lookAt(this.group.position);
    this.moon.lookAt(this.group.position);

    // Redden the sun near the horizon, the way atmosphere actually does.
    const sunMat = this.sun.material as THREE.MeshBasicMaterial;
    const lowness = Math.max(0, 1 - Math.abs(e) * 3.2);
    sunMat.color.setRGB(1, 1 - lowness * 0.22, 1 - lowness * 0.45);

    const stars = cycle.starVisibility;
    (this.starsFaint.material as THREE.PointsMaterial).opacity = stars * 0.75;
    (this.starsBright.material as THREE.PointsMaterial).opacity = stars;
    // The moon fades out in daylight rather than hanging there washed out.
    (this.moon.material as THREE.MeshBasicMaterial).opacity =
      0.35 + 0.65 * cycle.starVisibility;
  }
}
